import React, { useState, useCallback, Children, isValidElement } from 'react';

import { Link as TSRLink } from '@tanstack/react-router';
import cn from 'classnames';
import { Link as RRLink } from 'react-router-dom';

import { Dropdown } from '../Dropdown';
import Icon from '../Icon';
import { Pill } from '../Pill';

import Tab from './Tab';

import type { CreateLinkProps } from '@tanstack/react-router';

export interface TabsBaseProps {
  children: React.ReactNode;
  size: 'sm' | 'md';
  vertical?: boolean;
  type: 'button' | 'underline' | 'buttonBorder';
  className?: string;
  defaultTab?: string;
  linked?: 'tsr' | 'rr';
  route?: string;
  splitTab?: boolean;
  // When using TanStack Router links, control scroll reset behavior on navigation
  resetScroll?: boolean;
}

interface TabElementProps {
  label: string;
  linked: 'tsr' | 'rr' | undefined;
  path:
    | 'summary'
    | 'values'
    | 'rules'
    | 'site-section-configuration'
    | 'split-test'
    | 'running-experiments'
    | 'completed-experiments'
    | 'export-data'
    | undefined;
  className: string;
  onClick: () => void;
  children: React.ReactNode;
  resetScroll: boolean;
}

const mapTabsSize = {
  sm: 'text-sm',
  md: 'text-base',
};

const TabElement = ({
  label,
  linked,
  path,
  className,
  onClick,
  children,
  resetScroll,
}: TabElementProps) => {
  if (linked === 'tsr') {
    return (
      <TSRLink
        aria-labelledby={label}
        to="."
        search={
          ((prev: Record<string, unknown>) => ({
            ...prev,
            tab: path,
          })) as CreateLinkProps['search']
        }
        resetScroll={resetScroll}
        onClick={onClick}
        className={className}
      >
        {children}
      </TSRLink>
    );
  } else if (linked === 'rr') {
    return (
      <RRLink
        aria-labelledby={label}
        to={`?tab=${path}`}
        onClick={onClick}
        className={className}
      >
        {children}
      </RRLink>
    );
  } else {
    return (
      <>
        <button aria-labelledby={label} onClick={onClick} className={className}>
          {children}
        </button>
      </>
    );
  }
};

const TabsBase = ({
  children,
  size,
  vertical,
  type,
  className,
  defaultTab,

  linked,
  splitTab,
  resetScroll,
}: TabsBaseProps): JSX.Element => {
  const validChildren = Children.toArray(children).filter(
    (child) => isValidElement(child) && child.type === Tab,
  ) as any[];

  const tabs = validChildren.map((child) => {
    return child.props;
  });

  const validActiveTab = tabs[0].label;

  const queryTabExist = validChildren.find(
    (child) => child.props.label === defaultTab,
  );

  const initialTab = queryTabExist ? queryTabExist.props.label : validActiveTab;

  const [activeTab, setActiveTab] = useState(initialTab);
  const handleActiveTab = useCallback((label, trackClick) => {
    setActiveTab(label);
    if (trackClick) {
      trackClick();
    }
  }, []);

  const dropdownPanel = (
    <div className="sm:hidden">
      <Dropdown
        onChangeFunction={(value) => {
          handleActiveTab(
            value,
            tabs.find((tab) => tab.label === value).trackClick,
          );
        }}
        onChangeParameters={[]}
        value={activeTab}
      >
        {tabs.map((tab) => {
          return (
            <option key={tab.label} value={tab.label}>
              {tab.tabName}
            </option>
          );
        })}
      </Dropdown>
    </div>
  );

  const listPanel = (
    <div
      role="tablist"
      className={cn(
        {
          'border-sp-neutral-300 mb-4 border-b':
            vertical === false && type === 'underline',
          'bg-sp-neutral-50 border-sp-neutral-100 mr-4 rounded-md p-1':
            type === 'buttonBorder',
          'mr-4': vertical === true && type === 'button',
          'my-4 ml-6 mr-6':
            vertical !== true && type === 'button' && splitTab === false,
          'my-4 ml-6 mr-0': splitTab === true && type === 'button',
        },
        'hidden sm:block',
      )}
    >
      {tabs.map((tab) => {
        let badge;
        if (tab.badge !== null) {
          if (typeof tab.badge === 'number') {
            badge = (
              <span className="ml-2">
                <Pill
                  colour={
                    tab.label === activeTab && type === 'underline'
                      ? 'purple'
                      : 'gray'
                  }
                  text={tab.badge.toString()}
                />
              </span>
            );
          } else {
            badge = <span className="ml-2">{tab.badge}</span>;
          }
        } else {
          badge = null;
        }
        return (
          <TabElement
            label={tab.label}
            linked={linked}
            path={linked ? tab.label : null}
            resetScroll={resetScroll ?? true}
            onClick={() => handleActiveTab(tab.label, tab.trackClick)}
            className={cn(
              mapTabsSize[size],
              'mr-6',
              {
                'text-sp-primary bg-sp-primary-50':
                  tab.label === activeTab && type === 'button',
                'text-sp-primary border-sp-primary border-b-2':
                  vertical === false &&
                  tab.label === activeTab &&
                  type === 'underline',
                'text-sp-neutral bg-sp-white shadow':
                  tab.label === activeTab && type === 'buttonBorder',
                'inline-block': linked,
              },
              { 'text-sp-neutral-500 mr-6': tab.label !== activeTab },
              {
                'last:float-right': splitTab === true && type === 'button',
              },
              `mr-6 font-medium`,
              className,
            )}
            key={tab.label}
          >
            <span className={cn(`flex-col`)}>
              <span className={cn(`flex flex-row items-center justify-center`)}>
                {tab.iconName !== undefined && (
                  <Icon
                    className="mr-2"
                    size="xl"
                    name={tab.iconName}
                    color={'text-sp-primary'}
                  />
                )}
                <span>{tab.tabName}</span>

                {badge}
              </span>
            </span>
          </TabElement>
        );
      })}
    </div>
  );

  const tabContent = validChildren.find((child) => {
    return child.props.label === activeTab;
  });

  return (
    <div
      className={cn({
        'flex flex-row': vertical === true,
      })}
    >
      {dropdownPanel}
      {listPanel}
      {vertical === false && type === 'button' && (
        <div className="border-sp-neutral-200 border border-b"></div>
      )}

      <div className="w-full">{tabContent}</div>
    </div>
  );
};

TabsBase.defaultProps = {
  size: 'sm',
  vertical: false,
};

export default TabsBase;
